/**
 * USER API — lookup utenti via endpoint groupUsers Liferay.
 *
 * Il backend espone SOLO `GET /users/{groupId}` (lista Member del sito).
 * Niente `getById` o `search` server-side: il client li simula via cache
 * della lista groupUsers + filter/find in memoria. Pattern accettabile
 * perche':
 *   - la lista user di un sito CRM e' tipicamente piccola (<100)
 *   - viene usata solo come autocomplete per task.assignedUserId
 *   - il caching cross-call evita N fetch ripetute durante la stessa
 *     sessione (un task open + autocomplete = 1 sola GET)
 *
 * Cache module-level keyed per groupId. Esiste `invalidateCache(groupId?)`
 * per forzare il refetch (es. dopo un upsert utente, oppure su logout).
 */

import type { Page } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import type { User } from './userTypes';

/**
 * Shape del Member ritornato da Liferay backend (`it.dvel.crm.rest.dto.
 * v1_0.Member`). Usato solo internamente dal mapper Member -> User UI.
 */
export interface MemberDto {
  userId: number;
  screenName: string | null;
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  portraitURL: string | null;
  role: string | null;
  status: number | null;
  /** Assente quando l'utente non ha un codice gestionale (il backend lo omette). */
  externalReferenceCode?: string | null;
}

/**
 * Member "ricco" del sito, usato dalla lista di gestione utenti (Utenti e team):
 * a differenza di {@link User} mantiene firstName/lastName separati e il ruolo
 * CRM (ADMINISTRATOR/USER). portraitURL grezzo (da risolvere con resolvePortraitURL).
 */
export interface Member {
  userId: number;
  screenName: string;
  firstName: string;
  lastName: string;
  email: string;
  portraitURL?: string;
  role: string | null;
  /** Stato Liferay (WorkflowConstants): 0 = attivo (APPROVED), 5 = disattivato (INACTIVE). */
  status: number | null;
  /**
   * TS-139 — Codice dell'utente nel gestionale (externalReferenceCode), `null` se non ne ha uno.
   * Arriva già normalizzato dal backend (UserExternalReferenceCodeUtil): qui non si confronta
   * niente con l'uuid.
   */
  externalReferenceCode: string | null;
}

/**
 * Payload per la creazione di un utente (POST /users, operationId upsertUser).
 * `role` è il valore CRM (ADMINISTRATOR/USER); `memberships` i siti di cui
 * l'utente sarà membro (per la creazione dal pannello: il sito corrente).
 */
export interface CreateUserPayload {
  firstName: string;
  lastName: string;
  emailAddress: string;
  /**
   * FILO-75 — Nome utente (screen name) scelto dall'amministratore. Assente o vuoto: in creazione
   * lo genera Liferay, in modifica resta quello di prima. Gli errori di validazione (duplicato,
   * riservato, caratteri non ammessi) rispondono 409 col messaggio tradotto.
   */
  screenName?: string;
  role: string;
  memberships: { groupId: number }[];
}

/**
 * Payload per la modifica di un utente esistente (stesso endpoint POST /users,
 * operationId upsertUser): il campo `userId` valorizzato fa scattare il ramo di
 * aggiornamento lato backend. I `memberships` sovrascrivono i siti dell'utente:
 * dal pannello inviamo il solo sito corrente (scelta single-site).
 */
export interface UpdateUserPayload extends CreateUserPayload {
  userId: number;
}

/**
 * FILO-75 — Con che cosa si fa login nella company (`loginType` di GET /company/settings, dalle
 * impostazioni dell'istanza Liferay). Assente nelle risposte vecchie: vale l'e-mail, il default.
 */
export type LoginType = 'EMAIL_ADDRESS' | 'SCREEN_NAME' | 'USER_ID';

/** Mapper del Member ritornato dal backend verso il modello UI. */
export const toMember = (m: MemberDto): Member => ({
  userId: m.userId,
  screenName: m.screenName ?? '',
  firstName: m.firstName ?? '',
  lastName: m.lastName ?? '',
  email: m.emailAddress ?? '',
  portraitURL: m.portraitURL ?? undefined,
  role: m.role ?? null,
  status: m.status ?? null,
  externalReferenceCode: m.externalReferenceCode ?? null,
});

const cacheByGroup = new Map<number, Promise<User[]>>();

function fetchGroupUsers(groupId: number): Promise<User[]> {
  let cached = cacheByGroup.get(groupId);
  if (cached) return cached;
  cached = httpClient
    .get<Page<MemberDto>>(`/users/${groupId}`)
    .then((r) =>
      r.data.items.map((m): User => ({
        id: m.userId,
        screenName: m.screenName ?? '',
        fullName: `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || (m.emailAddress ?? `#${m.userId}`),
        email: m.emailAddress ?? '',
        portraitURL: m.portraitURL ?? undefined,
      })),
    );
  cacheByGroup.set(groupId, cached);
  // Su rejection rimuoviamo dalla cache: la prossima chiamata riprovera'
  // (altrimenti il fetch rimane "stuck" su rejected promise).
  cached.catch(() => cacheByGroup.delete(groupId));
  return cached;
}

export const userApi = {
  /** Lista completa user del sito. Cached. */
  getGroupUsers: (groupId: number): Promise<User[]> => fetchGroupUsers(groupId),

  /**
   * Lista dei Member del sito con firstName/lastName/role/portraitURL separati,
   * per la tabella di gestione utenti. Non cached: la lista va sempre fresca
   * (es. dopo un upsert utente).
   */
  getGroupMembers: (groupId: number): Promise<Member[]> =>
    httpClient
      .get<Page<MemberDto>>(`/users/${groupId}`)
      .then((r) => r.data.items.map(toMember)),

  /**
   * Crea un nuovo utente (POST /users). Il backend genera la password
   * temporanea, invia la mail di registrazione e assegna il ruolo CRM. Solo
   * per amministratori (enforce lato server). Ritorna il Member creato.
   */
  createUser: (payload: CreateUserPayload): Promise<Member> =>
    httpClient.post<MemberDto>('/users', payload).then((r) => toMember(r.data)),

  /**
   * Modifica un utente esistente (POST /users con `userId` valorizzato -> ramo
   * update del backend). Aggiorna nome/cognome/email e sincronizza il ruolo CRM.
   * Solo per amministratori (enforce lato server). Ritorna il Member aggiornato.
   */
  updateUser: (payload: UpdateUserPayload): Promise<Member> =>
    httpClient.post<MemberDto>('/users', payload).then((r) => toMember(r.data)),

  /**
   * Attiva/disattiva un utente (PATCH /user/{userId}/status). `status` è il valore
   * Liferay (WorkflowConstants): 0 = attivo (APPROVED), 5 = disattivato (INACTIVE).
   * Solo per amministratori (enforce lato server). Ritorna il Member aggiornato.
   */
  updateStatus: (userId: number, status: number): Promise<Member> =>
    httpClient
      .patch<MemberDto>(`/user/${userId}/status?status=${status}`)
      .then((r) => toMember(r.data)),

  /**
   * TS-139 — Codice gestionale di un utente (PATCH /user/{userId}/externalReferenceCode).
   * Stringa vuota = togli il codice. Un codice già di un altro utente risponde 409 col
   * messaggio tradotto. Solo per amministratori (enforce lato server).
   */
  updateExternalReferenceCode: (userId: number, externalReferenceCode: string): Promise<Member> =>
    httpClient
      .patch<MemberDto>(
        `/user/${userId}/externalReferenceCode?externalReferenceCode=${encodeURIComponent(externalReferenceCode)}`,
      )
      .then((r) => toMember(r.data)),

  /**
   * Lookup user by id. Risolto via cache groupUsers + find in memoria.
   * Lancia Error se l'id non e' nella lista del groupId (es. user
   * disattivato o non membro del sito).
   */
  getById: async (userId: number, groupId: number): Promise<User> => {
    const users = await fetchGroupUsers(groupId);
    const u = users.find((x) => x.id === userId);
    if (!u) throw new Error(`User ${userId} not found in group ${groupId}`);
    return u;
  },

  /**
   * Search case-insensitive su fullName/email. Filter in memoria sulla
   * lista cached. Query vuota -> ritorna tutta la lista.
   */
  search: async (query: string, groupId: number): Promise<User[]> => {
    const users = await fetchGroupUsers(groupId);
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  },

  /**
   * FILO-75 — Il tipo di login della company, dalle impostazioni (GET /company/settings, aperta a
   * ogni utente): la lista utenti mostra il nome utente o l'e-mail secondo come si entra.
   */
  getLoginType: (): Promise<LoginType> =>
    httpClient
      .get<{ loginType?: LoginType } | null>('/company/settings')
      .then((r) => r.data?.loginType ?? 'EMAIL_ADDRESS'),

  /** Invalida la cache (per groupId specifico o totale se omesso). */
  invalidateCache: (groupId?: number): void => {
    if (groupId == null) cacheByGroup.clear();
    else cacheByGroup.delete(groupId);
  },
};
